import React from 'react';
import PropTypes from 'prop-types';
import { IconButton } from '@cbrebuild/blocks';
import { useDispatch, useSelector } from 'react-redux';
import PercentDollarInput from '../../../nucleus/inputs/PercentDollarInput';
import { updateVoucher } from '../../../redux/actions/voucher/voucher-actions';
import useFormState, { EVENT_VALUE_TYPES } from '../../../hooks/useFormState';
import { deleteTaxAllocation, updateTaxAllocation } from '../../../redux/actions/tax-allocations-actions';
import FormGroup from '../../../nucleus/form-group/form-group';
import { selectTotalCommissionsForTaxAllocations } from '../../../redux/selectors/voucher-selectors';

const selectTaxAllocationById = (state, id) => (
  state.taxAllocations.taxAllocations.find(taxAllocation => taxAllocation.id === id)
);

const typeOverrides = {
  voucher_gross_commission_percent: EVENT_VALUE_TYPES.FLOAT,
  gross_commission: EVENT_VALUE_TYPES.FLOAT,
};

const TaxAllocationItem = ({
  id,
  voucherId,
  showValidation,
  tax_is_percent,
}) => {
  const taxAllocation = useSelector(state => selectTaxAllocationById(state, id)) || {};
  const baseTotal = useSelector(selectTotalCommissionsForTaxAllocations);
  const dispatch = useDispatch();

  const {
    tax_allocation_name, gross_commission, voucher_gross_commission_percent,
    onStateChanged, onStateChange,
  } = useFormState({
    tax_allocation_name: taxAllocation.tax_allocation_name,
    gross_commission: taxAllocation.gross_commission,
    voucher_gross_commission_percent: taxAllocation.voucher_gross_commission_percent,
  }, changes => dispatch(updateTaxAllocation(id, changes)), typeOverrides, { rowType: 'tax_allocation', rowId: id });

  const onUpdateVoucher = ({ target: { name, value } }) => {
    dispatch(updateTaxAllocation(id, {
      gross_commission: gross_commission.value || 0,
      voucher_gross_commission_percent: voucher_gross_commission_percent.value || 0,
    })).then(() => dispatch(updateVoucher(voucherId, { [name]: value })));
  };

  const onDelete = () => {
    dispatch(deleteTaxAllocation(id));
  };

  const invalidTax = (showValidation || tax_allocation_name.dirty) && !tax_allocation_name.value;
  const invalidGrossCommission = (showValidation || gross_commission.dirty) && !gross_commission.value;

  return (
    <div className="form-row-with-labels-and-hints">
      <FormGroup label="Tax" size="large" isRequired hasError={invalidTax} errorMessage="Required" className="due-upon-field">
        <input
          type="text"
          name="tax_allocation_name"
          value={tax_allocation_name.value || ''}
          onChange={onStateChange}
          onBlur={onStateChanged}
        />
      </FormGroup>
      <PercentDollarInput
        isRequired={invalidGrossCommission}
        label={`${invalidGrossCommission ? '' : '* '}amount`}
        name={{
          percentage: 'voucher_gross_commission_percent',
          value: 'gross_commission',
          select: 'tax_is_percent',
        }}
        className={invalidGrossCommission ? 'errorInput' : ''}
        onTypeChange={onUpdateVoucher}
        onChange={onStateChange}
        onBlur={onStateChanged}
        baseTotal={baseTotal}
        percentage={voucher_gross_commission_percent.value}
        value={gross_commission.value}
        isPercent={tax_is_percent}
      />
      <IconButton className="blxs-button-icon-small" iconName="close-circle" onClick={onDelete} variant="basic" />
    </div>
  );
};

TaxAllocationItem.propTypes = {
  id: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  showValidation: PropTypes.bool.isRequired,
  voucherId: PropTypes.number.isRequired,
  tax_is_percent: PropTypes.bool,
};

TaxAllocationItem.defaultProps = {
  tax_is_percent: false,
};

export default TaxAllocationItem;
